
import type { Ref } from 'vue'
import * as pdfMake from 'pdfmake/build/pdfmake'
import { getDocumentFooter } from '@/modules/Core/usings/pdf/pdfFooter';
import { getDocumentHeader } from '@/modules/Core/usings/pdf/pdfHeader';
import { getIps, removeLastPageBreak } from '@/modules/Core/usings/impresion'
import { styles } from '@/modules/Core/usings/pdf/pdfStyles'
import type { TDocumentDefinitions } from 'pdfmake/interfaces'
import { useReports } from '@/modules/Reportes/composables/useReports'
import { parseDocDefinition, parseJS } from '@/modules/Core/usings/generales';
import type { IConfDocjson, IResultadoReporteDocumento } from '@/modules/Configuracion/interfaces';
import type { ICitaDocjson } from '@/modules/Asistencial/interfaces';
import { personaService } from '@/services';
type FirmasPdf = { firmas: TDocumentDefinitions['content'], images: TDocumentDefinitions['images'], columnas?: unknown[] }
type DocumentoPdf = Pick<IConfDocjson, 'cofoCodigo' | 'cofoNombre' | 'cofoJson' | 'cofoStyle' | 'cofoHeader' | 'cofoFooter'> & Partial<Pick<IConfDocjson, 'reportes' | 'parametros'>>

const MARCADOR_FIRMA = /FIRMA_PROFESIONAL:(?:\d+|\{\{[^{}]*\}\})?/g
const PIXEL_TRANSPARENTE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII='

const firmaBase64 = async (copeId: string) => {
    try {
        const { data } = await personaService.getFirma({ copeId: Number(copeId), tipo: 'empleados' })
        const body = data?.body
        if (!body || body === 'No data') return null
        return body.startsWith('data:image') ? body : `data:image/png;base64,${body}`
    } catch {
        return null
    }
}

const MARCADOR_FIRMAS_MANUALES = /^FIRMAS_MANUALES(?::(\d+))?$/
const FIRMAS_POR_FILA = 3

const bloqueFirmasManuales = (columnas: unknown[], porFila: number) => {
    const filas: unknown[] = []
    for (let i = 0; i < columnas.length; i += porFila) {
        const fila = columnas.slice(i, i + porFila)
        while (fila.length < porFila) fila.push({ text: '' })
        filas.push({ columns: fila, columnGap: 5, margin: [0, 0, 0, 12] })
    }
    return { stack: filas }
}

const insertarFirmasManuales = (nodo: unknown, columnas: unknown[]): boolean => {
    if (Array.isArray(nodo)) {
        const i = nodo.findIndex(item => MARCADOR_FIRMAS_MANUALES.test(String((item as { text?: unknown })?.text ?? '')))
        if (i !== -1) {
            const marcador = { ...(nodo[i] as Record<string, unknown>) }
            const porFila = Number(MARCADOR_FIRMAS_MANUALES.exec(String(marcador.text))?.[1]) || FIRMAS_POR_FILA
            delete marcador.text
            nodo.splice(i, 1, ...(columnas.length ? [{ ...marcador, ...bloqueFirmasManuales(columnas, porFila) }] : []))
            return true
        }
        return nodo.some(item => insertarFirmasManuales(item, columnas))
    }
    if (nodo && typeof nodo === 'object') {
        return Object.values(nodo).some(valor => insertarFirmasManuales(valor, columnas))
    }
    return false
}

const resolverFirmasProfesional = async (cofoJson: string) => {
    const firmas = new Map<string, string>()
    for (const marcador of new Set([...cofoJson.matchAll(MARCADOR_FIRMA)].map(m => m[0]))) {
        const copeId = marcador.slice('FIRMA_PROFESIONAL:'.length)
        firmas.set(marcador, (/^\d+$/.test(copeId) ? await firmaBase64(copeId) : null) ?? PIXEL_TRANSPARENTE)
    }
    return cofoJson.replace(MARCADOR_FIRMA, marcador => firmas.get(marcador) ?? PIXEL_TRANSPARENTE)
}

export const useDocumentosPdf = () => {
    const { executeReport } = useReports()
    const generarStructuraCompleta = async (options: { doc: DocumentoPdf , withParams?: boolean, withHeader?: boolean, withFooter?: boolean, firmas?: FirmasPdf, reportesResueltos?: IResultadoReporteDocumento[] }) : Promise<TDocumentDefinitions> => {
        const { doc, withParams = false, firmas, withHeader = true, withFooter = true, reportesResueltos } = options
        const ips = await getIps()
        let cofoJson = doc.cofoJson
        if (withParams || reportesResueltos) {
            const reportesGenerados = reportesResueltos ?? await Promise.all(
                    (doc.reportes ?? []).map(async (reporte) => {
                        const datos = await executeReport(reporte.reporte)
                        return { coreId: reporte.reporte.coreId as number, datos: datos[0] || {} }
                    })
                )
            reportesGenerados.forEach(reporteGenerado => {
                if (reporteGenerado?.datos) {
                    const cabeceras = Object.keys(reporteGenerado.datos)
                    cabeceras.forEach(cabecera => {
                        const valor = reporteGenerado.datos[cabecera]
                        const valorSeguro = String(valor ?? '')
                            .replace(/\\/g, '\\\\')
                            .replace(/\r/g, '')
                            .replace(/\n/g, '\\n')
                            .replace(/'/g, "\\'")
                            .replace(/"/g, '\\"')
                            .replace(/`/g, '\\`')
                        cofoJson = cofoJson.replaceAll(`{{${reporteGenerado.coreId}_${cabecera}}}`, valorSeguro)
                        if (reportesResueltos) cofoJson = cofoJson.replaceAll(`{{${cabecera}}}`, valorSeguro)
                    })
                }
            })
            if (withParams) {
                (doc.parametros ?? []).filter(e => e.cdjpInForm && e.valor).forEach(param => {
                    cofoJson = cofoJson.replaceAll(`{{${param.cdjpNombre}}}`, (param.valor as string).replace(/\n/g, "\\n"))
                })
            }
        }
        cofoJson = await resolverFirmasProfesional(cofoJson)
        const mapDocDefinition = parseDocDefinition(cofoJson)
        const conEtiqueta = insertarFirmasManuales(mapDocDefinition.content, firmas?.columnas ?? [])
        if(firmas){
            if(!conEtiqueta) mapDocDefinition.content.push(firmas.firmas)
            mapDocDefinition.images = firmas.images
        }
        removeLastPageBreak(mapDocDefinition.content)
        if(withFooter) mapDocDefinition.footer = getDocumentFooter()
        if(withHeader) mapDocDefinition.header = getDocumentHeader(ips)
        mapDocDefinition.info = {
            title: doc.cofoNombre,
            author: ips.coemNombre
        }
        mapDocDefinition.styles = styles
        if(doc.cofoStyle && doc.cofoStyle != '{}') {
            mapDocDefinition.styles = parseJS(doc.cofoStyle as string)
        }
        mapDocDefinition.pageMargins ??= [40, (withHeader? 50: 0), 40, 50]
        
        return mapDocDefinition
    }
    const generarPdf = async ({ doc, withParams = false, firmas, reportesResueltos }: { doc: DocumentoPdf, withParams?: boolean, firmas?: FirmasPdf, reportesResueltos?: IResultadoReporteDocumento[] }) => {
        const mapDocDefinition = await generarStructuraCompleta({ doc, withParams, firmas, reportesResueltos, withHeader: doc.cofoHeader !== 0, withFooter: doc.cofoFooter !== 0 })
        return pdfMake.createPdf(mapDocDefinition)
    }
    const createPdf = async ( options: { doc: IConfDocjson , pdf: Ref<string | null>, withParams?: boolean, firmas?: FirmasPdf }) => {
        const { pdf, ...resto } = options
        const pdfDocGenerator = await generarPdf(resto)
        pdfDocGenerator.getBlob((blob: Blob) => {
            const url = URL.createObjectURL(blob); // blob:
            const old = pdf.value;
            pdf.value = url;
            if (old) URL.revokeObjectURL(old);      // evita fugas
        });
    }
    const openAndGeneratecreatePdf = async ( options: { doc: IConfDocjson , withParams?: boolean, firmas?: FirmasPdf }) => {
        const pdfDocGenerator = await generarPdf(options)
        pdfDocGenerator.open()
    }
     const openAndGeneratePdf = async (doc: ICitaDocjson | IConfDocjson) => {
        const mapDocDefinition = parseDocDefinition((doc as ICitaDocjson).cifoJson ? (doc as ICitaDocjson).cifoJson : (doc as IConfDocjson).cofoJson)
        pdfMake.createPdf(mapDocDefinition).open()
    }
    return {
        generarStructuraCompleta,
        generarPdf,
        createPdf,
        openAndGeneratePdf,
        openAndGeneratecreatePdf
    }
}
